export type SmsCommunication = {
  type: "sms";
  sender: string;
  receivedAt: string;
  body: string;
};

export type EmailCommunication = {
  type: "email";
  sender: string;
  subject: string;
  receivedAt: string;
  body: string;
};

export type Communication = SmsCommunication | EmailCommunication;

const PHONE_FRAME =
  "relative w-[300px] rounded-[2.75rem] border-[10px] border-neutral-900 bg-neutral-900 shadow-2xl";
const PHONE_SCREEN = "overflow-hidden rounded-[2rem]";
const NOTCH =
  "absolute left-1/2 top-0 z-10 h-6 w-32 -translate-x-1/2 rounded-b-2xl bg-neutral-900";

export function CommunicationMockup({
  communication,
  theme = "light",
  selectable = false,
}: {
  communication: Communication;
  theme?: "dark" | "light";
  /** Allow selecting/copying the mockup's text (sender, subject, body). Off by default — the mockup is normally a non-interactive visual preview. */
  selectable?: boolean;
}) {
  const isDark = theme === "dark";
  return (
    <div className={`${PHONE_FRAME} ${selectable ? "" : "select-none pointer-events-none"}`}>
      <div className={NOTCH} aria-hidden />
      <div className={PHONE_SCREEN}>
        {communication.type === "sms" ? (
          <SmsScreen data={communication} isDark={isDark} />
        ) : (
          <EmailScreen data={communication} isDark={isDark} />
        )}
      </div>
    </div>
  );
}

function StatusBar({ isDark = false }: { isDark?: boolean }) {
  const color = isDark ? "text-slate-100" : "text-neutral-900";
  return (
    <div className={`flex items-center justify-between px-6 pt-3 pb-1 text-[11px] font-semibold ${color}`}>
      <span>9:41</span>
      <span className="flex items-center gap-1">
        <span>••••</span>
        <span>5G</span>
        <span className="ml-1 inline-block h-2.5 w-4 rounded-sm border border-current">
          <span className="block h-full w-3/4 bg-current" />
        </span>
      </span>
    </div>
  );
}

function SmsScreen({ data, isDark }: { data: SmsCommunication; isDark: boolean }) {
  const screenBg    = isDark ? "bg-[#1c1f26]"    : "bg-white";
  const divider     = isDark ? "border-slate-700" : "border-neutral-200";
  const avatarBg    = isDark ? "bg-slate-700"     : "bg-neutral-200";
  const avatarText  = isDark ? "text-slate-200"   : "text-neutral-700";
  const senderName  = isDark ? "text-slate-100"   : "text-neutral-900";
  const smsLabel    = isDark ? "text-slate-500"   : "text-neutral-500";
  const bubbleBg    = isDark ? "bg-slate-700"     : "bg-neutral-200";
  const bubbleText  = isDark ? "text-slate-100"   : "text-neutral-900";
  const inputBg     = isDark ? "bg-[#141820]"     : "bg-neutral-50";
  const inputBorder = isDark ? "border-slate-700" : "border-neutral-300";

  return (
    <div className={`flex h-[560px] flex-col ${screenBg}`}>
      <StatusBar isDark={isDark} />
      <div className={`flex flex-col items-center border-b px-4 pb-3 pt-2 ${divider}`}>
        <div className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold ${avatarBg} ${avatarText}`}>
          {initials(data.sender)}
        </div>
        <div className={`mt-1 text-[13px] font-medium ${senderName}`}>{data.sender}</div>
        <div className={`text-[10px] uppercase tracking-wide ${smsLabel}`}>SMS</div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        <div className={`text-center text-[10px] ${smsLabel}`}>{data.receivedAt}</div>
        <div className="flex justify-start">
          <div className={`max-w-[80%] whitespace-pre-line rounded-2xl rounded-bl-md px-3.5 py-2 text-[13px] leading-snug ${bubbleBg} ${bubbleText}`}>
            {linkify(data.body, isDark)}
          </div>
        </div>
      </div>

      <div className={`border-t px-3 py-2 ${divider}`}>
        <div className={`h-8 rounded-full border ${inputBorder} ${inputBg}`} />
      </div>
    </div>
  );
}

function EmailScreen({ data, isDark }: { data: EmailCommunication; isDark: boolean }) {
  const screenBg    = isDark ? "bg-[#1c1f26]"    : "bg-white";
  const divider     = isDark ? "border-slate-700" : "border-neutral-200";
  const navText     = isDark ? "text-blue-400"    : "text-blue-600";
  const navArrows   = isDark ? "text-slate-500"   : "text-neutral-400";
  const subjectText = isDark ? "text-slate-100"   : "text-neutral-900";
  const avatarBg    = isDark ? "bg-blue-900"      : "bg-blue-100";
  const avatarText  = isDark ? "text-blue-300"    : "text-blue-700";
  const senderName  = isDark ? "text-slate-100"   : "text-neutral-900";
  const metaText    = isDark ? "text-slate-500"   : "text-neutral-500";
  const bodyText    = isDark ? "text-slate-200"   : "text-neutral-800";
  const actionText  = isDark ? "text-blue-400"    : "text-blue-600";

  return (
    <div className={`flex h-[560px] flex-col ${screenBg}`}>
      <StatusBar isDark={isDark} />
      <div className={`flex items-center justify-between border-b px-4 py-2 text-[12px] ${divider} ${navText}`}>
        <span>‹ Inbox</span>
        <span className={navArrows}>⌄ ⌃</span>
      </div>

      <div className={`border-b px-4 py-3 ${divider}`}>
        <h4 className={`text-[15px] font-semibold leading-snug ${subjectText}`}>
          {data.subject}
        </h4>
        <div className="mt-2 flex items-start gap-2">
          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${avatarBg} ${avatarText}`}>
            {initials(data.sender)}
          </div>
          <div className="min-w-0 flex-1">
            <div className={`truncate text-[12px] font-medium ${senderName}`}>
              {data.sender}
            </div>
            <div className={`text-[11px] ${metaText}`}>
              To: me · {data.receivedAt}
            </div>
          </div>
        </div>
      </div>

      <div className={`flex-1 overflow-y-auto px-4 py-3 text-[13px] leading-relaxed ${bodyText}`}>
        <p className="whitespace-pre-line">{linkify(data.body, isDark)}</p>
      </div>

      <div className={`flex items-center justify-around border-t py-2 text-[18px] ${divider} ${actionText}`}>
        <span>↩</span>
        <span>↩↩</span>
        <span>→</span>
        <span>🗑</span>
      </div>
    </div>
  );
}

function initials(s: string) {
  const clean = s.replace(/[<>"]/g, "").trim();
  const parts = clean.split(/[\s@.]+/).filter(Boolean);
  return (parts[0]?.[0] ?? "?").toUpperCase() + (parts[1]?.[0]?.toUpperCase() ?? "");
}

function linkify(text: string, isDark = false) {
  const parts = text.split(/(https?:\/\/\S+)/g);
  return parts.map((p, i) =>
    /^https?:\/\//.test(p) ? (
      <span key={i} className={`break-all underline ${isDark ? "text-blue-400" : "text-blue-600"}`}>
        {p}
      </span>
    ) : (
      <span key={i}>{p}</span>
    ),
  );
}
